import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import Button from '~/src/common/components/Button';
import GooglePlacesAutocomplete, {
  GooglePlacesAutocompleteType,
} from '~/src/common/components/GooglePlacesAutocomplete';
import ProspectEmailForm from '~/src/common/components/ProspectEmailForm';
import { useGoogleMapsScriptTag } from '~/src/common/hooks/useGoogleMapsScriptTag';
import { useApiEcomClientError } from '~/src/common/services/error-handler/error-handler';
import I18n from '~/src/common/services/I18n';
import { showNiceModal } from '~/src/common/services/ModalsManager';
import Tracker, {
  getDeliveryZoneProperties,
  sendShippingSlotValidatedEvent,
  setTimeSlotUserProperties,
} from '~/src/common/services/Tracker';
import { DeliveryAddress } from '~/src/common/typings/cart';
import { isApiEcomError } from '~/src/common/utils/guards';
import { getPreferredTimeSlotTimestamp } from '~/src/common/utils/slots';
import {
  deliverySlots,
  getGetCartQueryKey,
  getRecompute2QueryKey,
  usePatchCartDelivery2,
} from '~/src/queries/api-ecom/generated/api-ecom';
import { getNextAvailableSlot, getSlotFromStartHour } from '~/src/screens/Landing/utils';

import { PropsFromInputs } from '../../types';

import { Container, InputWrapper, NoDelivery, Overlay } from './layout';

type Props = PropsFromInputs<typeof import('./inputs').default>;

export default (_props: Props) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const handleError = useApiEcomClientError();
  const cartDelivery = usePatchCartDelivery2();
  const { isGoogleMapsLoaded } = useGoogleMapsScriptTag();
  const [isLoadingAddress, setLoadingAddress] = useState(false);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [address, setAddress] = useState<DeliveryAddress>();

  // Mode Click & Collect : Ouverture modale + Redirection vers la home
  const handleClickPickup = async () => {
    await router.push({ pathname: '/', query: router.query });

    void showNiceModal('pickup-address-modal');
  };

  // Mode saisie d'une adresse
  const handleSelectResult = async (address: DeliveryAddress) => {
    setLoadingAddress(true);
    try {
      // Récupération de la zone de livraison associée
      const { location, addressComponents } = address;
      const { postalCode, countryCode } = addressComponents;
      const data = await deliverySlots({ postalCode, countryCode, location });
      const deliveryZone = data.deliveryZones.find(({ type }) => type === 'delivery');

      // Si pas de zone de livraison :
      // - Ouvre le dropdown pour le C&C et la newsletter
      // - Enregistre l'adresse pour la modale de sélection des créneaux
      if (deliveryZone == null) {
        setOverlayVisible(true);
        setAddress(address);
        Tracker.sendEvent('shipping type refused', {
          'shipping type': 'delivery',
          'shipping type step': 'enter the address',
          'event source': 'navigation',
        });
      }

      // Si une zone de livraison existe :
      // - Enregistre l'adresse pour la modale de sélection des créneaux
      // - Sélectionne automatiquement le créneau de livraison le plus adapté
      else {
        setAddress(address);

        const zoneProperties = getDeliveryZoneProperties(deliveryZone);
        Tracker.setUserProperties(zoneProperties);
        Tracker.sendEvent('shipping type validated', {
          'validation mode': 'manual',
          'event source': 'navigation',
          ...zoneProperties,
        });
        // Sélection automatique d'un créneau
        const timeSlot = getNextAvailableSlot(
          getSlotFromStartHour(deliveryZone.deliverySlots, [0, 23]),
          getPreferredTimeSlotTimestamp(deliveryZone.preferredTimeSlotSchedule),
        );
        // Mise à jour du créneau
        if (timeSlot == null) return;
        setTimeSlotUserProperties(timeSlot);
        sendShippingSlotValidatedEvent({ timeSlot, deliveryZone, reasonForChange: 'new address' });
        await cartDelivery.mutateAsync({ data: { delivery: { note: '', address }, timeSlot } });
        // Rechargement du cart, et direction home
        await Promise.all([
          queryClient.refetchQueries(getGetCartQueryKey()),
          queryClient.refetchQueries(getRecompute2QueryKey()),
        ]);
        void router.push({ pathname: '/', query: { ...router.query, deliverySlotToast: true } });
      }
    } catch (error) {
      if (isApiEcomError(error)) handleError(error);
    }
    setLoadingAddress(false);
  };

  return (
    <Container>
      <InputWrapper>
        {isGoogleMapsLoaded ? (
          <GooglePlacesAutocomplete
            // Configuration
            isGeolocationEnabled
            type={GooglePlacesAutocompleteType.address}
            isLoadingAddress={isLoadingAddress}
            disabled={!isGoogleMapsLoaded}
            autoFocus={false}
            // Handlers
            onSelectResult={addr => void handleSelectResult(addr)}
            onClear={() => setOverlayVisible(false)}
            onChange={() => setOverlayVisible(false)}
            // Internationalisation
            inputProps={{ placeholder: I18n.t('google-places-autocomplete.placeholder-address') }}
          />
        ) : null}
        {isOverlayVisible ? (
          <Overlay>
            {address ? (
              <ProspectEmailForm
                address={address}
                handleClickCollectShopsButton={() => void handleClickPickup()}
              />
            ) : (
              <NoDelivery>
                <p>{I18n.translate({ value: 'landing.input.no-delivery' })}</p>
                <p>
                  <Button
                    variant="link"
                    rightIcon="Arrow-foreward"
                    iconSize={15}
                    onClick={() => void handleClickPickup()}>
                    {I18n.t('landing.input.button-label')}
                  </Button>
                </p>
              </NoDelivery>
            )}
          </Overlay>
        ) : null}
      </InputWrapper>
    </Container>
  );
};
